import React, { useContext, useState } from 'react'
import { AlertTriangle, Flag, RefreshCw, X } from 'lucide-react'
import { BASE_URL_API } from '../../References/urls'
import AuthContext from '../../store/auth-context'
import styles from './FlagFraudModal.module.css'

function fmtAmount(amount) {
    if (amount == null) return '—'
    return `₹${Number(amount).toLocaleString('en-IN')}`
}

const FlagFraudModal = ({ prefillReferralId = '', onClose, onDone }) => {
    const authCtx = useContext(AuthContext)
    const [referralId, setReferralId] = useState(prefillReferralId)
    const [reason, setReason] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')

    const canSubmit = referralId.trim() && reason.trim()

    const handleSubmit = async () => {
        if (!canSubmit) return
        setError('')
        setIsSubmitting(true)
        try {
            const res = await fetch(BASE_URL_API + '/referralFlagFraud', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    adminId: authCtx.userIdentifier,
                    referralId: referralId.trim(),
                    fraudFlagReason: reason.trim(),
                }),
            })
            const data = await res.json()
            if (data.success === true) {
                const held = data.response?.rewardsHeld
                const msg = held?.count
                    ? `Flagged. ${fmtAmount(held.totalAmount)} rewards held (${held.count}).`
                    : 'Fraud flag applied.'
                onDone(msg, false)
            } else {
                const errType = data.response?.errorType
                if (errType === 'ALREADY_FLAGGED') {
                    setError('This referral is already fraud-flagged.')
                } else if (errType === 'NOT_FOUND') {
                    setError('Referral ID not found.')
                } else if (errType === 'MISSING_FIELDS') {
                    setError('Referral ID and reason are required.')
                } else {
                    if (data.message === 'token Expired, please login again!') authCtx.logout()
                    else setError(data.message || 'Could not flag referral. Try again.')
                }
            }
        } catch {
            setError('Network error. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <span className={styles.modalTitle}>Flag for Fraud Review</span>
                    <button className={styles.iconBtn} onClick={onClose} disabled={isSubmitting}><X size={13} /></button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.formField}>
                        <label className={styles.formLabel}>Referral ID</label>
                        <input
                            className={`${styles.formInput} ${styles.formInputMono}`}
                            value={referralId}
                            onChange={e => setReferralId(e.target.value)}
                            placeholder="REF-20240820-AB3CD"
                            disabled={isSubmitting || !!prefillReferralId}
                        />
                    </div>
                    <div className={styles.warningBanner}>
                        <AlertTriangle size={12} />
                        <span>Pending rewards will be held until the flag is cleared. Existing available balance is not affected.</span>
                    </div>
                    <div className={styles.formField}>
                        <label className={styles.formLabel}>Reason</label>
                        <textarea
                            className={styles.formTextarea}
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="e.g. same_device_as_referrer — Device fingerprint match detected on registration"
                            disabled={isSubmitting}
                            rows={3}
                        />
                    </div>
                    {error && <div className={styles.errorText}><AlertTriangle size={11} />{error}</div>}
                </div>
                <div className={styles.modalFooter}>
                    <button
                        className={styles.btnDanger}
                        onClick={handleSubmit}
                        disabled={isSubmitting || !canSubmit}
                    >
                        {isSubmitting
                            ? <><RefreshCw size={12} className={styles.spinIcon} /> Flagging...</>
                            : <><Flag size={12} /> Flag This Referral</>}
                    </button>
                    <button className={styles.btnSecondary} onClick={onClose} disabled={isSubmitting}>Cancel</button>
                </div>
            </div>
        </div>
    )
}

export default FlagFraudModal
